import { Modifier, StoreGroup } from '@/types/menus.types';
import { FranchiseeBundleAddOnStoreGroupPrice, FranchiseeModifierNamedPriceRow } from '@/types/namedPrice.types';
import { RowSelectionState } from '@tanstack/react-table';
import { hasRelevantActivePriceInStoreGroup } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';

function buildStoreGroupPriceFromRange(range: string): FranchiseeBundleAddOnStoreGroupPrice {
  const trimmed = range?.trim() ?? '';
  const isRange = trimmed.includes('-');
  const priceDisplay = isRange
    ? trimmed
        .split('-')
        .map((s) => (s.trim().startsWith('$') ? s.trim() : s.trim() ? `$${s.trim()}` : ''))
        .filter(Boolean)
        .join(' - ') || (trimmed.startsWith('$') ? trimmed : `$${trimmed}`)
    : trimmed.startsWith('$')
    ? trimmed
    : trimmed
    ? `$${trimmed}`
    : '';
  return {
    priceDisplay,
    isRange,
    chosenPriceRange: trimmed,
  };
}

/** Map PUT /modifiers named-price aggregated rows into table row model. */
export function mapModifiersToNamedPriceRows(modifiers: Modifier[]): FranchiseeModifierNamedPriceRow[] {
  return modifiers
    .filter((modifier) => Boolean(modifier.namedPriceId))
    .map((modifier) => {
      const storeGroupPrices: Record<string, FranchiseeBundleAddOnStoreGroupPrice> = {};
      for (const sg of modifier.storeGroups ?? []) {
        storeGroupPrices[sg.storeGroupName] = buildStoreGroupPriceFromRange(sg.chosenPriceRange ?? '');
      }
      return {
        namedPriceId: modifier.namedPriceId as string,
        name: modifier.namedPriceName || modifier.modifierName || '',
        isLock: Boolean(modifier.priceIsLocked),
        isVisible: modifier.isVisible,
        isMandatory: modifier.isMandatory,
        isZeroPriced: modifier.isZeroPriced,
        storeGroupPrices,
      };
    });
}

/** Map a named-price row to Modifier shape for FZMenuModifierTable and client-side filters. */
export function mapNamedPriceRowToModifierTableRow(row: FranchiseeModifierNamedPriceRow): Modifier {
  const storeGroups: StoreGroup[] = Object.entries(row.storeGroupPrices).map(([storeGroupName, priceInfo]) => ({
    storeGroupId: '',
    storeGroupName,
    stores: [],
    chosenPriceRange: priceInfo.chosenPriceRange ?? priceInfo.priceDisplay.replace(/\$/g, '').trim(),
    minValidPrice: 0,
    maxValidPrice: 0,
  }));

  return {
    modifierId: row.namedPriceId,
    slotId: row.namedPriceId,
    modifierWeightId: row.namedPriceId,
    modifierName: row.name,
    modifierDescription: '',
    priceIsLocked: row.isLock,
    isVisible: row.isVisible,
    isMandatory: row.isMandatory,
    isZeroPriced: row.isZeroPriced,
    namedPriceId: row.namedPriceId,
    namedPriceName: row.name,
    storeGroups,
  };
}

export function mapNamedPriceRowsToModifierTableRows(rows: FranchiseeModifierNamedPriceRow[]): Modifier[] {
  return rows.map(mapNamedPriceRowToModifierTableRow);
}

export function getNamedPriceRowsFromRowSelection(
  selectedRows: RowSelectionState,
  rows: FranchiseeModifierNamedPriceRow[],
): FranchiseeModifierNamedPriceRow[] {
  return Object.keys(selectedRows)
    .map((index) => rows[Number(index)])
    .filter((row): row is FranchiseeModifierNamedPriceRow => row != null);
}

/**
 * Raw PUT /modifiers rows for selected named prices (keeps storeGroups[].stores for deactivate scope).
 * Sets modifierName from namedPriceName for the confirmation table.
 */
export function getRawModifiersForNamedPriceSelection(
  selectedNamedPriceRows: FranchiseeModifierNamedPriceRow[],
  menuModifiers: Modifier[],
): Modifier[] {
  const selectedIds = new Set(selectedNamedPriceRows.map((row) => row.namedPriceId));
  return menuModifiers
    .filter((modifier) => modifier.namedPriceId && selectedIds.has(modifier.namedPriceId))
    .map((modifier) => ({
      ...modifier,
      modifierName: modifier.namedPriceName || modifier.modifierName || '',
    }));
}

const NAMED_PRICE_INACTIVE_DEFAULT = 99;

/** True when deactivation falls back to a catalog default below 99, so the drawer shows a price. */
export function namedPriceShowsGlobalDefaultPrice(defaultPrice: number | null | undefined): boolean {
  const price = defaultPrice == null ? Number.NaN : Number(defaultPrice);
  return !Number.isNaN(price) && price < NAMED_PRICE_INACTIVE_DEFAULT;
}

/** Future cell after named-price deactivate: Inactive when the catalog default is missing or >= 99. */
export function namedPriceDeactivateFutureLabel(
  defaultPrice: number | null | undefined,
  inactiveLabel: string,
): string {
  if (!namedPriceShowsGlobalDefaultPrice(defaultPrice)) {
    return inactiveLabel;
  }
  return `$${Number(defaultPrice).toFixed(2)}`;
}

export function getNamedPriceRowFingerprint(rows: FranchiseeModifierNamedPriceRow[]): string {
  return rows.map((row) => row.namedPriceId).join('|');
}

/** Invisible row with an active price in at least one selected store group — edit/activate blocked. */
export function namedPriceRowHasInvisibleActivePriceState(row: FranchiseeModifierNamedPriceRow): boolean {
  if (row.isVisible !== false) return false;
  return Object.values(row.storeGroupPrices).some((priceInfo) =>
    hasRelevantActivePriceInStoreGroup(priceInfo.chosenPriceRange),
  );
}
